
export default class Color {
  static brightness(color, percent, alpha=null) {
    if(!color){
      return color
    }

    let R, G, B, A = 1;

    if (color?.startsWith("#")) {
      if (!/^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(color)) {
        return color;
      }
      R = parseInt(color.substring(1, 3), 16);
      G = parseInt(color.substring(3, 5), 16);
      B = parseInt(color.substring(5, 7), 16);

      if (color.length === 9) {
        A = parseInt(color.substring(7, 9), 16) / 255;
      }
    } else if (color?.startsWith("rgb")) {
      // RGB or RGBA format
      const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d*\.?\d+))?\)/);
      if (!match) return color; // Invalid RGB(A) format

      R = parseInt(match[1]);
      G = parseInt(match[2]);
      B = parseInt(match[3]);
      if (match[4] !== undefined) {
        A = parseFloat(match[4]);
      }
    } else {
      return color; // Unsupported format
    }

    R = Math.min(255, Math.max(0, Math.round(R * (100 + percent) / 100)));
    G = Math.min(255, Math.max(0, Math.round(G * (100 + percent) / 100)));
    B = Math.min(255, Math.max(0, Math.round(B * (100 + percent) / 100)));

    if (typeof alpha === "string" && /^[0-9a-fA-F]{2}$/.test(alpha)) {
      A = parseInt(alpha, 16) / 255;
    } else if (typeof alpha === "number" && alpha >= 0 && alpha <= 1) {
      A = alpha;
    }

    if (color?.startsWith("#")) {
      let alphaHex = Math.round(A * 255).toString(16).padStart(2, '0');
      return `#${R.toString(16).padStart(2, '0')}${G.toString(16).padStart(2, '0')}${B.toString(16).padStart(2, '0')}${alphaHex}`;
    } else {
      return `rgba(${R}, ${G}, ${B}, ${A.toFixed(2)})`;
    }
  }

  static saturate(color, saturationPercent) {
    if (saturationPercent < 0 || saturationPercent > 100) {
      return color;
    }

    let R, G, B, A = 1;

    if (color?.startsWith("#")) {
      // HEX format
      if (!/^#([0-9a-f]{6})$/i.test(color)) {
        return color;
      }
      R = parseInt(color.substr(1, 2), 16) / 255;
      G = parseInt(color.substr(3, 2), 16) / 255;
      B = parseInt(color.substr(5, 2), 16) / 255;
      if (color.length === 9) {
        A = parseInt(color.substr(7, 2), 16) / 255;
      }
    } else if (color?.startsWith("rgb")) {
      // RGB or RGBA format
      const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d*\.?\d+))?\)/);
      if (!match) return color;
      R = parseInt(match[1]) / 255;
      G = parseInt(match[2]) / 255;
      B = parseInt(match[3]) / 255;
      if (match[4] !== undefined) {
        A = parseFloat(match[4]);
      }
    } else {
      return color; // Unsupported format
    }

    let saturationFloat = saturationPercent / 100;
    let rgbIntensityFloat = [R, G, B];

    let rgbIntensityFloatSorted = [...rgbIntensityFloat].sort((a, b) => a - b);
    let maxIntensityFloat = rgbIntensityFloatSorted[2];
    let mediumIntensityFloat = rgbIntensityFloatSorted[1];
    let minIntensityFloat = rgbIntensityFloatSorted[0];

    if (maxIntensityFloat === minIntensityFloat) {
      return color; // Gray color, no saturation change
    }

    let newMinIntensityFloat = maxIntensityFloat * (1 - saturationFloat);
    let newMediumIntensityFloat;

    if (mediumIntensityFloat === minIntensityFloat) {
      newMediumIntensityFloat = newMinIntensityFloat;
    } else {
      let intensityProportion = (maxIntensityFloat - mediumIntensityFloat) / (mediumIntensityFloat - minIntensityFloat);
      newMediumIntensityFloat = (intensityProportion * newMinIntensityFloat + maxIntensityFloat) / (intensityProportion + 1);
    }

    let newRgbIntensityFloatSorted = [newMinIntensityFloat, newMediumIntensityFloat, maxIntensityFloat];
    let newRgbIntensityFloat = rgbIntensityFloat.map(originalRgb => {
      let rgbSortedIndex = rgbIntensityFloatSorted.indexOf(originalRgb);
      return newRgbIntensityFloatSorted[rgbSortedIndex];
    });

    let floatToHex = val => ('0' + Math.round(val * 255).toString(16)).substr(-2);
    let rgb2hex = (rgb, alpha) => `#${floatToHex(rgb[0])}${floatToHex(rgb[1])}${floatToHex(rgb[2])}${alpha < 1 ? floatToHex(alpha) : ''}`;
    let rgb2rgbString = (rgb, alpha) => alpha < 1 ? `rgba(${Math.round(rgb[0] * 255)}, ${Math.round(rgb[1] * 255)}, ${Math.round(rgb[2] * 255)}, ${alpha.toFixed(2)})` : `rgb(${Math.round(rgb[0] * 255)}, ${Math.round(rgb[1] * 255)}, ${Math.round(rgb[2] * 255)})`;

    return color?.startsWith("#") ? rgb2hex(newRgbIntensityFloat, A) : rgb2rgbString(newRgbIntensityFloat, A);
  }

  static addAlpha(color, alpha) {
    let A = 1;

    if (typeof alpha === "string" && /^[0-9a-fA-F]{2}$/.test(alpha)) {
      A = parseInt(alpha, 16) / 255;
    } else if (typeof alpha === "number" && alpha >= 0 && alpha <= 1) {
      A = alpha;
    } else {
      return color; // Invalid alpha
    }

    if (color?.startsWith("#")) {
      if (!/^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(color)) {
        return color;
      }
      let alphaHex = Math.round(A * 255).toString(16).padStart(2, '0');
      return `#${color.substring(1, 7)}${alphaHex}`;
    }else if (color?.startsWith("rgb")) {
      const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d*\.?\d+))?\)/);
      if (!match) return color;
      const R = parseInt(match[1]);
      const G = parseInt(match[2]);
      const B = parseInt(match[3]);
      return `rgba(${R}, ${G}, ${B}, ${A.toFixed(2)})`;
    }else{
      return color;
    }
  }

  static isLight(color) {

    // Variables for red, green, blue values
    var r, g, b, hsp;

    // Check the format of the color, HEX or RGB?
    if (color.match(/^rgb/)) {

        // If RGB --> store the red, green, blue values in separate variables
        color = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/);

        r = color[1];
        g = color[2];
        b = color[3];
    }
    else {

        // If hex --> Convert it to RGB: http://gist.github.com/983661
        color = +("0x" + color.slice(1).replace(
        color.length < 5 && /./g, '$&$&'));

        r = color >> 16;
        g = color >> 8 & 255;
        b = color & 255;
    }

    // HSP (Highly Sensitive Poo) equation from http://alienryderflex.com/hsp.html
    hsp = Math.sqrt(
    0.299 * (r * r) +
    0.587 * (g * g) +
    0.114 * (b * b)
    );

    // Using the HSP value, determine whether the color is light or dark
    return hsp>150
  }
}
